import React, { useState, useCallback } from 'react';
import { EditContactModal } from './EditContactModal';
import { Table, Badge, Button } from '@crisellevaldez/speculo-ui';
import { ExpandableTagsCell } from '../../common/ExpandableTagsCell';
import { Pencil, Trash, Tags, CheckCircle2, XCircle } from 'lucide-react';
import { AskRemiButton } from './AskRemiButton';
import { LeadScoreDisplay } from './LeadScoreDisplay';
import { Contact, Column, ContactTableProps } from '../../../types/contact.types';
import {
  getIntentStyle,
  getLeadSourceStyle,
  IntentType,
  LeadSourceType,
} from '../../../utils/badgeStyles';
import { OutcomeBadge } from '../../common/OutcomeBadge';
import { formatDate, formatOptInDate } from '../../../utils/dateTime';
import { formatPhoneNumber } from '../../../utils/formatters';
import { ConfirmationDialog } from '../../common/dialogs';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { useOrganizationUsers } from '../../../hooks/useOrganizationUsers';
import { AssignedAgentCell } from '../../common/AssignedAgentCell';

export const ContactTable: React.FC<ContactTableProps> = ({
  contacts = [],
  isLoading,
  selectedContacts = [],
  onSelectAll,
  onSelectContact,
  onEdit,
  onDelete,
  onSort,
  onDeleteSelected,
  onAddTags,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  const [contactToEdit, setContactToEdit] = useState<Contact | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const organization = useOrganizationStore((state) => state.organization);
  const { users: orgUsers } = useOrganizationUsers();
  const [updatedContacts, setUpdatedContacts] = useState<{ [id: string]: Contact }>({});

  const handleOpenEditModal = useCallback((contact: Contact) => {
    setContactToEdit(contact);
    setShowEditModal(true);
  }, []);

  const handleDeleteClick = useCallback((contact: Contact) => {
    setContactToDelete(contact);
    setShowDeleteModal(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    setIsDeleting(true);
    try {
      if (contactToDelete) {
        await onDelete?.(contactToDelete);
      } else {
        await onDeleteSelected?.();
      }
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setContactToDelete(null);
    }
  }, [contactToDelete, onDelete, onDeleteSelected]);

  const handleDeleteSelectedClick = useCallback(() => {
    setContactToDelete(null);
    setShowDeleteModal(true);
  }, []);

  const handleSelectRows = useCallback(
    (rows: string[]) => {
      if (rows.length === contacts.length) {
        onSelectAll?.(true);
      } else if (rows.length === 0) {
        onSelectAll?.(false);
      } else {
        const addedRows = rows.filter((id) => !selectedContacts.includes(id));
        const removedRows = selectedContacts.filter((id) => !rows.includes(id));

        addedRows.forEach((id) => onSelectContact?.(id, true));
        removedRows.forEach((id) => onSelectContact?.(id, false));
      }
    },
    [contacts, selectedContacts, onSelectAll, onSelectContact]
  );

  const handleSort = useCallback(
    (key: string, direction: 'asc' | 'desc') => {
      onSort?.(key, direction);
    },
    [onSort]
  );

  const columns: Column<Contact>[] = [
    // 1. Name (pinned left)
    {
      key: 'name',
      header: 'Full Name',
      sortable: true,
      width: '200px',
      isPinned: true,
      pinPosition: 'left',
      cell: (item: Contact) => {
        const fullName = `${item.firstName || ''} ${item.lastName || ''}`.trim() || '-';
        return (
          <div className="flex items-center gap-3">
            <AskRemiButton
              contact={item}
              size="sm"
              onClick={(contact) => {
                console.log('Summarize contact:', contact);
              }}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEditModal(item);
              }}
              className={`text-primaryfortext font-medium ${fullName !== '-' ? 'underline' : ''} text-left`}
            >
              {fullName}
            </button>
          </div>
        );
      },
    },
    // 2. Contact (Phone + Email merged)
    {
      key: 'phoneNumber',
      header: 'Contact',
      sortable: true,
      resizable: true,
      width: '220px',
      cell: (item: Contact) => {
        const hasPhone = !!item.phoneNumber;
        const phone = hasPhone ? formatPhoneNumber(item.phoneNumber) : '';
        const email = item.email;
        if (!hasPhone && !email) {
          return <span className="text-muted-foreground">-</span>;
        }
        return (
          <div className="flex flex-col gap-0.5">
            {hasPhone && (
              <a
                href={`tel:${item.phoneNumber}`}
                onClick={(e) => e.stopPropagation()}
                className="text-foreground hover:text-primary hover:underline transition-colors w-fit"
              >
                {phone}
              </a>
            )}
            {email && (
              <div className="flex items-center gap-1.5">
                <a
                  href={`mailto:${email}`}
                  onClick={(e) => e.stopPropagation()}
                  className="t-caption truncate hover:text-primary hover:underline transition-colors"
                >
                  {email}
                </a>
                {item.email_status && (
                  <Badge
                    variant={
                      ['active', 'valid', 'verified'].includes(
                        (item.email_status ?? '').toLowerCase(),
                      )
                        ? 'success'
                        : 'destructive'
                    }
                    className="px-1.5 py-0"
                  >
                    {item.email_status}
                  </Badge>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    // 3. Lead Score
    {
      key: 'score',
      header: 'Lead Score',
      sortable: true,
      resizable: true,
      width: '140px',
      isCentered: true,
      cell: (item: Contact) => {
        const contactToUse = updatedContacts[item.id] || item;
        return (
          <div className="flex justify-center">
            <LeadScoreDisplay
              contact={contactToUse}
              size="sm"
              onScoreCalculated={(updatedContact) => {
                setUpdatedContacts((prev) => ({
                  ...prev,
                  [updatedContact.id]: updatedContact,
                }));
              }}
            />
          </div>
        );
      },
    },
    // 4. Tags
    {
      key: 'tags',
      header: 'Tags',
      sortable: false,
      resizable: true,
      width: '300px',
      cell: (item: Contact) => {
        const tags = item.tags;
        if (!tags) return <span className="text-muted-foreground">-</span>;
        if (typeof tags === 'object' && !Array.isArray(tags) && Object.keys(tags).length === 0)
          return <span className="text-muted-foreground">-</span>;
        if (Array.isArray(tags)) {
          return <ExpandableTagsCell tags={tags} />;
        }
        return String(tags);
      },
    },
    // 5. Source — original origination (CRM, manual, etc.)
    {
      key: 'leadSource',
      header: 'Source',
      sortable: true,
      resizable: true,
      width: '160px',
      cell: (item: Contact) => {
        const source = item.leadSource?.trim();
        if (!source) return <span className="text-muted-foreground">-</span>;
        return <span className="whitespace-nowrap text-foreground">{source}</span>;
      },
    },
    // 6. Intent + Lead Source (Speculo campaign signal — Buyer/Seller + PPC/Email/Organic)
    {
      key: 'leadIntent',
      header: 'Intent / Lead Source',
      sortable: false,
      resizable: true,
      width: '220px',
      cell: (item: Contact) => {
        const intent = item.leadIntent?.trim();
        const leadSourceSecondary = item.leadSourceSecondary?.trim();
        if (!intent && !leadSourceSecondary) return <span className="text-muted-foreground">-</span>;
        const intentStyle = intent ? getIntentStyle(intent as IntentType) : null;
        const sourceStyle = leadSourceSecondary
          ? getLeadSourceStyle(leadSourceSecondary as LeadSourceType)
          : null;
        return (
          <div className="flex flex-wrap gap-1">
            {intent && intentStyle && (
              <Badge className={intentStyle.background}>{intent}</Badge>
            )}
            {leadSourceSecondary && sourceStyle && (
              <Badge className={sourceStyle.background}>{leadSourceSecondary}</Badge>
            )}
          </div>
        );
      },
    },
    // 7. Opt In
    {
      key: 'opt_in_date',
      header: 'Opt In',
      sortable: true,
      resizable: true,
      width: '210px',
      cell: (item: Contact) => {
        if (!item.opt_in) {
          return <XCircle className="t-icon-sm text-red-500 dark:text-red-400" />;
        }
        const formattedOptInDate = formatOptInDate(item.opt_in_date);
        return (
          <div className="flex items-center gap-1.5 whitespace-nowrap">
            <CheckCircle2 className="t-icon-sm text-green-600 dark:text-green-400" />
            {formattedOptInDate && <span className="text-foreground">{formattedOptInDate}</span>}
          </div>
        );
      },
    },
    // 8. Last Call (combined: timestamp first, outcome badge after, on one line)
    {
      key: 'lastCallAnswered',
      header: 'Last Call',
      sortable: true,
      resizable: true,
      width: '300px',
      cell: (item: Contact) => {
        const outcome = item.recentOutcome;
        const answeredAt = item.lastCallAnswered;
        if (!outcome && !answeredAt) return <span className="text-muted-foreground">-</span>;
        return (
          <div className="flex items-center gap-2 whitespace-nowrap">
            {answeredAt && (
              <span className="text-foreground">
                {formatDate(
                  new Date(answeredAt),
                  organization?.timezone || 'UTC',
                  'MMM D, YYYY h:mm A z'
                )}
              </span>
            )}
            {outcome && <OutcomeBadge outcome={outcome} />}
          </div>
        );
      },
    },
    // 9. Flows
    {
      key: 'flows',
      header: 'Flows',
      sortable: false,
      resizable: true,
      width: '200px',
      cell: (item: Contact & { flows?: string[] }) => {
        const flows = (item.flows || []).filter((name) => name && name.trim().length > 0);
        if (flows.length === 0) return <span className="text-muted-foreground">-</span>;
        return <ExpandableTagsCell tags={flows} badgeClassName="badge-blue" />;
      },
    },
    // 10. Assigned To (CRM owner)
    {
      key: 'assignedAgent',
      header: 'Assigned To',
      sortable: false,
      resizable: true,
      width: '300px',
      cell: (item: Contact) => (
        <AssignedAgentCell
          agent={item.assignedAgent}
          agentId={item.assignedAgentId}
          pondId={item.assignedPondId}
          orgUsers={orgUsers}
        />
      ),
    },
    // 7. Actions (pinned right)
    {
      key: 'actions',
      header: 'Actions',
      width: '90px',
      isCentered: true,
      isPinned: true,
      pinPosition: 'right',
      cell: (item: Contact) => (
        <div className="flex items-center justify-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleOpenEditModal(item)}
            className="p-1.5 hover:bg-accent"
            disabled={isDeleting}
          >
            <Pencil className="w-3.5 h-3.5 text-foreground" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDeleteClick(item)}
            className="p-1.5 hover:bg-accent"
            disabled={isDeleting}
          >
            <Trash className="w-3.5 h-3.5 text-foreground" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="overflow-hidden bg-white dark:bg-transparent">
      {selectedContacts.length > 0 && (
        <div className="flex justify-center md:justify-start items-center gap-4 p-3 border-b border-border">
          <span className="t-label">
            <span className="font-bold text-foreground">{selectedContacts.length}</span> contact
            {selectedContacts.length > 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onAddTags}
              className="flex items-center gap-2 h-8"
              disabled={isDeleting}
            >
              <Tags className="w-4 h-4" />
              Add Tags
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDeleteSelectedClick}
              className="flex items-center gap-2 text-white hover:bg-red-600 bg-red-700 border-0 h-8"
              disabled={isDeleting}
              isLoading={isDeleting}
            >
              <Trash className="w-4 h-4" />
              Delete Selected
            </Button>
          </div>
        </div>
      )}

      <div className="h-[calc(100vh-200px)] 3xl:h-[calc(100vh-250px)]">
        <Table
          data={contacts.map((contact) => updatedContacts[contact.id] || contact)}
          columns={columns}
          keyExtractor={(item) => item.id}
          sortable={true}
          onSort={handleSort}
          selectable={true}
          selectedRows={selectedContacts}
          onSelectRows={handleSelectRows}
          loading={isLoading}
          size="sm"
          emptyStateText="No contacts available"
          onRowDoubleClick={handleOpenEditModal}
        />
      </div>
      <ConfirmationDialog
        isOpen={showDeleteModal}
        onClose={() => {
          if (!isDeleting) {
            setShowDeleteModal(false);
            setContactToDelete(null);
          }
        }}
        onConfirm={handleConfirmDelete}
        title={contactToDelete ? 'Delete Contact' : 'Delete Selected Contacts'}
        message={
          contactToDelete
            ? `Are you sure you want to delete ${contactToDelete.firstName || ''} ${
                contactToDelete.lastName || ''
              }?`.trim()
            : `Are you sure you want to delete ${selectedContacts.length} selected contact${
                selectedContacts.length > 1 ? 's' : ''
              }?`
        }
        confirmLabel="Delete"
        isLoading={isDeleting}
        variant="danger"
      />
      <EditContactModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setContactToEdit(null);
        }}
        onSubmit={async (data) => {
          if (contactToEdit) {
            const updatedContact: Contact = {
              ...contactToEdit,
              ...data,
              updatedAt: new Date().toISOString(),
            };
            await onEdit?.(updatedContact);
            setShowEditModal(false);
            setContactToEdit(null);
          }
        }}
        contact={contactToEdit}
      />
    </div>
  );
};

export default ContactTable;
